import React from 'react';
import styles from './PaymentTable.module.scss';
import CustomTable from '../../../Common/CustomTable';
import AvatarIcon from '../../../Common/AvatarIcon';
import { ReactComponent as EyeIcon } from '../../../../assets/svg/eye_gray.svg';
import { modalActions } from '../../../Modals/store';
import { useDispatch } from 'react-redux';
import IconButton from '../../../Common/Buttons/IconButton';
import clx from 'classnames';
import { setCategory } from '../constants';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

const openPaymentDetail = (dispatch, original) => {
  dispatch(
    modalActions.OPEN_MODAL.SUCCESS({
      type: 'showInfoPaymentModal',
      props: { pagamento: original, setCategory },
    }),
  );
};

export default ({
  values,
  isMobile,
  setPage,
  total,
  serverSidePagination = true,
  disableRimborsa = false,
  pageRows = 20,
  entityId,
}) => {
  const { t } = useTranslation();
  const data = React.useMemo(() => values, [values]);
  const dispatch = useDispatch();
  const columns = React.useMemo(
    () =>
      isMobile
        ? [
            {
              Header: '',
              accessor: 'actions',
              Cell: ({ row: { original } }) => {
                const today = dayjs().format('YYYY-MM-DD');
                const data = dayjs(original.data_prenotazione).format('YYYY-MM-DD');
                const diff = dayjs(data).diff(today, 'day');
                const servicesLabel = original.servizi?.[0]?.nomeServizio
                  ? `${original.servizi[0].nomeServizio}${
                      original.servizi.length > 1
                        ? ` ${t('payments.table.mobile.servicesPlus', {
                            count: original.servizi.length - 1,
                          })}`
                        : ''
                    }`
                  : original.servizi?.length > 1
                    ? t('payments.table.mobile.servicesPlus', { count: original.servizi.length - 1 })
                    : '';
                return (
                  <button
                    type="button"
                    className={clx(styles.payment_mobile_card, {
                      [styles.payment_mobile_card_past]: diff < 0,
                    })}
                    aria-label={t('payments.table.details')}
                    onClick={() => openPaymentDetail(dispatch, original)}
                  >
                    <AvatarIcon name={original.clientName} icon={original.icon} size="40px" />
                    <div className={styles.mobile_copy}>
                      <span className={styles.payment_name}>{original.clientName}</span>
                      <span className={styles.payment_meta}>
                        {dayjs(original.created_at).format('DD/MM/YYYY')}
                        {servicesLabel ? ` · ${servicesLabel}` : ''}
                      </span>
                    </div>
                    {!disableRimborsa && (
                      <span
                        className={clx(styles.amount_pill, {
                          [styles.amount_pill_negative]: original.totalePagato < 0,
                        })}
                      >
                        €{original.totalePagato.toFixed(2)}
                      </span>
                    )}
                    <ChevronRight size={18} strokeWidth={2.2} />
                  </button>
                );
              },
            },
          ]
        : [
            {
              Header: t('payments.table.headers.paymentDate'),
              accessor: 'created_at',
              Cell: ({ row: { original } }) => {
                const today = dayjs().format('YYYY-MM-DD');
                const data = dayjs(original.data_prenotazione).format('YYYY-MM-DD');
                const diff = dayjs(data).diff(today, 'day');
                return (
                  <div className={styles.payment}>
                    <span
                      className={clx(styles.date_mark, { [styles.date_mark_past]: diff < 0 })}
                    />
                    <span className={styles.payment_date}>
                      {dayjs(original.created_at).format('DD/MM/YYYY')}
                    </span>
                  </div>
                );
              },
            },
            {
              Header: t('payments.table.headers.client'),
              accessor: 'cliente',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.name_cell_container}>
                    <AvatarIcon name={original.clientName} icon={original.icon} size="40px" />
                    <div className={styles.name}>{original.clientName}</div>
                  </div>
                );
              },
            },
            {
              Header: t('payments.table.headers.service'),
              accessor: 'Servizio',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.payment_services}>
                    {original.servizi?.map((x) => x.nomeServizio).join(', ')}
                  </div>
                );
              },
            },
            {
              Header: t('payments.table.headers.chair'),
              accessor: 'Poltrona',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.payment_services}>
                    {original.poltrona?.nome_referente}
                  </div>
                );
              },
            },
            {
              Header: t('payments.table.headers.bookingDate'),
              accessor: 'data_prenotazione',
              Cell: ({ row: { original } }) => {
                return (
                  <div className={styles.payment_date}>
                    {original.data_prenotazione
                      ? dayjs(original.data_prenotazione).format('DD/MM/YYYY')
                      : t('payments.table.notAvailable')}
                  </div>
                );
              },
            },
            {
              Header: t('payments.table.headers.paid'),
              accessor: 'importo',
              Cell: ({ row: { original } }) => {
                const tot = original.importo + original.voucher ?? 0;
                return (
                  <div
                    className={clx(styles.payment_value, {
                      [styles.payment_value_negative]: tot < 0,
                    })}
                  >
                    €{tot?.toFixed(2)}
                  </div>
                );
              },
            },
            {
              Header: t('payments.table.headers.coupon'),
              accessor: 'voucher',
              Cell: ({ row: { original } }) => {
                return (
                  <div
                    className={clx(styles.payment_value, {
                      [styles.payment_value_muted]: original.voucher == 0,
                    })}
                  >
                    {original.voucher > 0 ? `€${original.voucher?.toFixed(2)}` : '—'}
                  </div>
                );
              },
            },
            {
              Header: '',
              accessor: '*',
              Cell: ({ row: { original } }) => {
                return (
                  <IconButton
                    svg={<EyeIcon />}
                    type={'default'}
                    size={'32px'}
                    onClick={() => openPaymentDetail(dispatch, original)}
                  />
                );
              },
            },
          ],
    [isMobile, t, disableRimborsa, dispatch],
  );

  return (
    <div
      className={clx(styles.table_card, {
        [styles.table_card_mobile]: isMobile,
      })}
    >
      <CustomTable
        data={data}
        columns={columns}
        pageCount={total}
        setPaginationValue={setPage}
        serverSidePagination={serverSidePagination}
        isMobile={isMobile}
        pageRows={pageRows}
        entityId={entityId}
      />
    </div>
  );
};
